/**
 * Selecting and removing the consumer's added quote. The server saves the quote as a policy funding
 * source and records the selection; on success the returned plan replaces the cached plan and the
 * returned client goes into Redux, which recalculates the plan's coverage. Both run in the plan-save
 * scope, so they reach the server in order with every plan save.
 */
import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { fetchClientByClientIdResponse } from '@/model';
import { consumerApi, consumerPlanKeys } from '../api/consumer-api';

export function useAddedQuote(clientId: string) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const scope = { id: `consumer-plan-${clientId}` };

  const applyResponse = useCallback(
    (response: ConsumerAddedQuoteResponse) => {
      queryClient.setQueryData<ConsumerPlanResponse>(
        consumerPlanKeys.detail(clientId),
        response.plan,
      );
      dispatch(fetchClientByClientIdResponse(response.clientContainer));
    },
    [clientId, dispatch, queryClient],
  );

  const add = useMutation({
    mutationFn: (quoteKey: string) => consumerApi.putAddedQuote(clientId, quoteKey),
    scope,
    onSuccess: applyResponse,
  });
  const remove = useMutation({
    mutationFn: () => consumerApi.deleteAddedQuote(clientId),
    scope,
    onSuccess: applyResponse,
  });

  return {
    addQuote: add.mutateAsync,
    removeAddedQuote: remove.mutateAsync,
    addingQuoteKey: add.isPending ? add.variables : null,
    isRemoving: remove.isPending,
    addFailed: add.isError,
    removeFailed: remove.isError,
  };
}
